import cx from 'classnames'
import { FC } from 'react'

import { ActionButtonVariant } from '../../components/ActionButton/styled'
import { LinkProps } from '../../components/Link'
import { FormFieldAutoSuggestProps } from '../../form/fields/FormFieldAutoSuggest'
import FormFieldDropdownSelect, {
  FormFieldDropdownSelectProps,
} from '../../form/fields/FormFieldDropdownSelect'
import FormFieldInput, {
  FormFieldInputProps,
} from '../../form/fields/FormFieldInput'
import FormikField from '../../form/FormikField'
import { FormikFormProps } from '../../form/FormikForm'

import FormFieldDate from 'src/form/fields/FormFieldDate'
import * as SC from './styled'

export type AdsSearchFormValues = {
  offerType?: Array<number | string>
  specialities?: Array<number | string>
  localization?: string
  department?: Array<number | string>
  radius?: string
  lat?: string
  lng?: string
  term?: string
  startDate?: string
  endDate?: string
  days?: Array<string>
}

export type AdsSearchFormProps = {
  className?: string
  isOpen: boolean
  isLoading: boolean
  texts?: {
    offerTypeTitle?: string
    specialityTitle?: string
    localizationTitle?: string
    periodTitle?: string
    periodHelp?: string
    daysTitle?: string
    daysHelp?: string
    departmentTitle?: string
    radiusTitle?: string
    termTitle?: string
    submitButton: string
  }
  form: {
    showPeriodAndDays?: boolean
    formikForm: FormikFormProps<AdsSearchFormValues>
    fields: {
      offersType?: Omit<FormFieldDropdownSelectProps, 'name'>
      specialities?: Omit<FormFieldDropdownSelectProps, 'name'>
      localization?: Omit<FormFieldAutoSuggestProps, 'name'>
      startDate?: Omit<FormFieldInputProps, 'name'>
      endDate?: Omit<FormFieldInputProps, 'name'>
      days?: Omit<FormFieldDropdownSelectProps, 'name'>
      department?: Omit<FormFieldDropdownSelectProps, 'name'>
      radius?: Omit<FormFieldDropdownSelectProps, 'name'>
      term?: Omit<FormFieldInputProps, 'name'>
    }
    hideSpecialities?: boolean
    submitButtonVariant?: ActionButtonVariant
  }
  removeFilters?: LinkProps
}

const AdsSearchForm: FC<AdsSearchFormProps> = ({
  className,
  form,
  texts,
  isLoading,
  removeFilters,
}) => {
  const { fields, formikForm, showPeriodAndDays } = form

  return (
    <SC.Filters className={cx('AdsSearchForm', className)}>
      <SC.Form {...formikForm}>
        <SC.Grid $department={fields?.department}>
          {fields.offersType && (
            <SC.OfferTypeBlock>
              <SC.BlockTitle id="offerTypeLabel">
                {texts?.offerTypeTitle}
              </SC.BlockTitle>
              <FormikField
                Component={FormFieldDropdownSelect}
                {...fields.offersType}
                aria-labelledby="offerTypeLabel"
                name={'offerType'}
              />
            </SC.OfferTypeBlock>
          )}
          {fields.specialities && (
            <SC.SpecialitiesBlock>
              {!form.hideSpecialities && (
                <>
                  <SC.BlockTitle id="specialityLabel">
                    {texts?.specialityTitle}
                  </SC.BlockTitle>
                  <FormFieldDropdownSelect
                    {...fields.specialities}
                    aria-labelledby="specialityLabel"
                    name={'specialities'}
                  />
                </>
              )}
            </SC.SpecialitiesBlock>
          )}
          {fields.localization && fields.radius && (
            <SC.LocalisationAndRadiusWrapper>
              <SC.LocalizationBlock>
                <SC.BlockTitle id="localizationLabel">
                  {texts?.localizationTitle}
                </SC.BlockTitle>
                <FormikField
                  {...fields.localization}
                  name={'localization'}
                  aria-labelledby="localizationLabel"
                  Component={SC.AutoSuggestField}
                />
                <FormikField hidden name={'lat'} Component={SC.HiddenField} />
                <FormikField hidden name={'lng'} Component={SC.HiddenField} />
              </SC.LocalizationBlock>
              <SC.RadiusBlock>
                <SC.RadiusBlockTitle id="radiusLabel">
                  {texts?.radiusTitle}
                </SC.RadiusBlockTitle>
                <FormFieldDropdownSelect
                  {...fields.radius}
                  name={'radius'}
                  aria-labelledby="radiusLabel"
                />
              </SC.RadiusBlock>
            </SC.LocalisationAndRadiusWrapper>
          )}
          {fields.department && (
            <SC.DepartmentBlock>
              <SC.BlockTitle id="departmentLabel">
                {texts?.departmentTitle}
              </SC.BlockTitle>
              <FormFieldDropdownSelect
                {...fields.department}
                name={'department'}
                aria-labelledby="departmentLabel"
              />
            </SC.DepartmentBlock>
          )}
          {fields.startDate && fields.endDate && showPeriodAndDays && (
            <SC.PeriodBlock>
              <SC.BlockTitleWrapper>
                <SC.BlockTitle id="periodLabel">
                  {texts?.periodTitle}
                </SC.BlockTitle>
                <SC.BlockHelp>{texts?.periodHelp}</SC.BlockHelp>
              </SC.BlockTitleWrapper>
              <SC.PeriodBlockControl>
                <FormikField
                  Component={FormFieldDate}
                  {...fields.startDate}
                  aria-labelledby="daysLabel"
                  name="startDate"
                />
                au
                <FormikField
                  Component={FormFieldDate}
                  {...fields.endDate}
                  aria-labelledby="periodToLabel"
                  name="endDate"
                />
              </SC.PeriodBlockControl>
            </SC.PeriodBlock>
          )}
          {fields.days && showPeriodAndDays && (
            <SC.DaysBlock>
              <SC.BlockTitleWrapper>
                <SC.BlockTitle id="daysLabel">{texts?.daysTitle}</SC.BlockTitle>
                <SC.BlockHelp>{texts?.daysHelp}</SC.BlockHelp>
              </SC.BlockTitleWrapper>
              <FormikField
                Component={FormFieldDropdownSelect}
                {...fields.days}
                aria-labelledby="daysLabel"
                name="days"
              />
            </SC.DaysBlock>
          )}
          {fields.term && (
            <SC.TermeBlock $showPeriodAndDays={showPeriodAndDays}>
              <SC.BlockTitle id="termLabel">{texts?.termTitle}</SC.BlockTitle>
              <FormikField
                Component={FormFieldInput}
                {...fields.term}
                name={'term'}
                autoComplete="off"
                aria-labelledby="termLabel"
              />
            </SC.TermeBlock>
          )}
        </SC.Grid>
        <SC.Wrapper>
          {removeFilters && <SC.RemoveFilters {...removeFilters} />}
          <SC.Submit
            text={texts?.submitButton}
            variant={form.submitButtonVariant}
            isLoading={isLoading}
          />
        </SC.Wrapper>
      </SC.Form>
    </SC.Filters>
  )
}

export default AdsSearchForm
